import React from "react";
import { Badge, Box, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow, Text } from "braid-design-system";
import { getBomVersions, RestaurantBomResponse } from "../../services/bomService";

interface BomVersionHistoryProps {
    itemCode: string;
    active: boolean;
}

const formatDate = (value?: string | null): string => {
    if (!value) return "-";
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};

const InfoPanel: React.FC<{ tone?: "secondary" | "critical"; children: React.ReactNode }> = ({ tone = "secondary", children }) => (
    <Box padding="large" background="neutralLight" borderRadius="standard">
        <Text tone={tone} align="center">
            {children}
        </Text>
    </Box>
);

const BomVersionHistory: React.FC<BomVersionHistoryProps> = ({ itemCode, active }) => {
    const [versions, setVersions] = React.useState<RestaurantBomResponse[] | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (!active) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        getBomVersions(itemCode)
            .then((result) => {
                if (!cancelled) setVersions(result);
            })
            .catch((err: any) => {
                if (!cancelled) setError(err?.message || "Failed to load BOM history");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [itemCode, active]);

    if (!active) return null;
    if (loading) return <InfoPanel>Loading version history...</InfoPanel>;
    if (error) return <InfoPanel tone="critical">{error}</InfoPanel>;
    if (!versions || versions.length === 0) return <InfoPanel>No BOM versions saved yet.</InfoPanel>;

    return (
        <Table label="BOM version history">
            <TableHeader>
                <TableRow>
                    <TableHeaderCell>Version</TableHeaderCell>
                    <TableHeaderCell>BOM Code</TableHeaderCell>
                    <TableHeaderCell align="center">Status</TableHeaderCell>
                    <TableHeaderCell align="right">Ingredients</TableHeaderCell>
                    <TableHeaderCell align="right">Total Cost</TableHeaderCell>
                    <TableHeaderCell>Saved By</TableHeaderCell>
                    <TableHeaderCell>Saved At</TableHeaderCell>
                </TableRow>
            </TableHeader>
            <TableBody>
                {versions.map((version) => (
                    <TableRow key={version.bom_id}>
                        <TableCell>
                            <Text weight="strong">{`V${version.version_no}`}</Text>
                        </TableCell>
                        <TableCell>
                            <Text size="small" tone="secondary">{version.bom_code}</Text>
                        </TableCell>
                        <TableCell align="center">
                            {version.status === 1 ? (
                                <Badge tone="positive">Active</Badge>
                            ) : (
                                <Badge tone="neutral">Superseded</Badge>
                            )}
                        </TableCell>
                        <TableCell align="right">
                            <Text size="small">{version.details.length}</Text>
                        </TableCell>
                        <TableCell align="right">
                            <Text size="small">{`Rs. ${Number(version.total_cost || 0).toFixed(2)}`}</Text>
                        </TableCell>
                        <TableCell>
                            <Text size="small">{version.created_by || "-"}</Text>
                        </TableCell>
                        <TableCell>
                            <Text size="small">{formatDate(version.created_at)}</Text>
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
};

export default BomVersionHistory;
